import axios from 'axios';
import moment from 'moment';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
console.log('API URL:', API_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to every request
api.interceptors.request.use(
  (config) => {
    // Only log important requests or when debugging specific issues
    // Removed verbose logging to reduce console noise
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    // Only log errors and important endpoints, not every single response
    if (response.status >= 400) {
      console.log(`⚠️ Response: ${response.status} ${response.config.method.toUpperCase()} ${response.config.url}`);
    }
    
    return response;
  },
  (error) => {
    if (error.response) {
      console.error(`❌ Response error: ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      console.error('Error response:', error.response.data);
      
      // Handle unauthorized errors (expired token, invalid token, etc.)
      if (error.response.status === 401) {
        console.log('Authentication error detected (401 Unauthorized), clearing storage and redirecting to login');
        // Clear all localStorage data
        localStorage.clear();
        // Clear any session storage as well
        sessionStorage.clear();
        // Force redirect to login page
        window.location.href = '/login';
        return; // Don't continue processing this error
      }
      
      // Also handle 403 Forbidden as it might indicate token issues
      if (error.response.status === 403) {
        console.log('Access forbidden (403), checking if token is invalid');
        const errorMessage = error.response.data?.message?.toLowerCase() || '';
        if (errorMessage.includes('token') || errorMessage.includes('unauthorized') || errorMessage.includes('invalid')) {
          console.log('Token-related 403 error detected, logging out user');
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
          return;
        }
      }
    } else if (error.request) {
      console.error('❌ No response received:', error.request);
    } else {
      console.error('❌ Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Authentication services
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  updatePassword: (data) => {
    // Create a custom axios instance without global interceptors
    // to avoid automatic redirect on 401 errors
    const customAxios = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Add the authentication token
    const token = localStorage.getItem('token');
    if (token) {
      customAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    console.log('Using custom axios instance for password update to prevent auto-logout');
    
    // Return the custom axios promise
    return customAxios.post('/auth/update-password', data)
      .catch(error => {
        console.error('Password update error:', error.response?.status, error.response?.data);
        // Re-throw the error for component-level handling
        throw error;
      });
  },
};

// API Credentials service
export const apiCredentialsService = {
  getLanoCredentials: () => api.get('/api-credentials/lano'),
};

// User services
export const userService = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (userData) => api.put('/users/profile', userData),
  createUser: (userData) => api.post('/users', userData),
  getUserPreferences: (email) => api.get(`/users/preferences/${email}`),
  savePreferences: (email, preferences) => api.put(`/users/filter-preferences`, { email, ...preferences }),
  getAllUsers: () => api.get('/users'),
  getAllUsersWithTalents: () => api.get('/users/with-talents'),
  getUserById: (id) => api.get(`/users/${id}`),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  updateUserTalents: (id, talentData) => api.put(`/users/${id}/talents`, talentData),
  addTalentToUser: (id, talentData) => api.post(`/users/${id}/talents`, talentData),
  resetPassword: (id, passwordData) => api.put(`/users/${id}/reset-password`, passwordData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  updateFilterPreferences: (preferences) => api.put('/users/filter-preferences', preferences),
};

// Talent services
export const talentService = {
  getAllTalents: (page = 1, limit = 1000, filterParams = {}) => {
    // Make sure we're accessing the base talents endpoint
    let url = `/talents?page=${page}&limit=${limit}`;
    
    // Add any additional filter parameters
    if (filterParams.function) url += `&function=${encodeURIComponent(filterParams.function)}`;
    if (filterParams.location) url += `&location=${encodeURIComponent(filterParams.location)}`;
    if (filterParams.search) url += `&search=${encodeURIComponent(filterParams.search)}`;
    if (filterParams.set) url += `&set=${encodeURIComponent(filterParams.set)}`;
    
    console.log('Fetching talents with URL:', url);
    return api.get(url);
  },
  searchTalents: (params) => api.get('/talents/search', { params }),
  getTalentById: (id) => {
    if (!id) {
      console.error('Invalid talent ID provided:', id);
      return Promise.reject(new Error('Invalid talent ID'));
    }
    return api.get(`/talents/${id}`);
  },
  createTalent: (talentData) => api.post('/talents', talentData),
  duplicateTalent: (id) => api.post(`/talents/${id}/duplicate`),
  updateTalent: (id, talentData) => api.patch(`/talents/${id}`, talentData),
  updateTalentStatus: (id, status) => api.patch(`/talents/${id}/status`, { status }),
  deleteTalent: (id) => api.delete(`/talents/${id}`),
  createBulkRelationships: (talentIds) => {
    if (!Array.isArray(talentIds) || talentIds.length < 2) {
      console.error('Invalid talent IDs for bulk relationships:', talentIds);
      return Promise.reject(new Error('At least 2 talent IDs are required'));
    }
    console.log('Creating bulk relationships for talents:', talentIds);
    return api.post('/talents/bulk-relationships', { talent_ids: talentIds });
  },
  getTalentsByFunction: (functionName, page = 1, limit = 20) => {
    return api.get(`/talent?function=${encodeURIComponent(functionName)}&page=${page}&limit=${limit}`);
  },
  getTalentsByLocation: (location, page = 1, limit = 20) => {
    return api.get(`/talents?location=${encodeURIComponent(location)}&page=${page}&limit=${limit}`);
  },
  
  // New company relationship endpoints
  getTalentCompanies: (talentId) => {
    if (!talentId) {
      console.error('Invalid talent ID provided for company relationships:', talentId);
      return Promise.reject(new Error('Invalid talent ID'));
    }
    return api.get(`/talent-companies/${talentId}`);
  },
  
  updateTalentCompanies: (talentId, companyData) => {
    if (!talentId) {
      console.error('Invalid talent ID provided for updating company relationships:', talentId);
      return Promise.reject(new Error('Invalid talent ID'));
    }
    return api.patch(`/talent-companies/${talentId}`, companyData);
  },
  
  updateGoodPerformanceCompanies: (talentId, companies) => {
    if (!talentId) {
      console.error('Invalid talent ID provided for updating good performance companies:', talentId);
      return Promise.reject(new Error('Invalid talent ID'));
    }
    return api.patch(`/talent-companies/${talentId}/good-performance`, { companies });
  },
  
  updateBadPerformanceCompanies: (talentId, companies) => {
    if (!talentId) {
      console.error('Invalid talent ID provided for updating bad performance companies:', talentId);
      return Promise.reject(new Error('Invalid talent ID'));
    }
    return api.patch(`/talent-companies/${talentId}/bad-performance`, { companies });
  },
  
  updateTryoutCompanies: (talentId, companies) => {
    if (!talentId) {
      console.error('Invalid talent ID provided for updating tryout companies:', talentId);
      return Promise.reject(new Error('Invalid talent ID'));
    }
    return api.patch(`/talent-companies/${talentId}/tryout`, { companies });
  },
};

// Booking services
export const bookingService = {
  getAllBookings: (page = 1, limit = 9000, from = null, to = null, statuses = null, excludeStatuses = null) => {
    let url = `/bookings?page=${page}&limit=${limit}`;
    if (from) url += `&from=${from}`;
    if (to) url += `&to=${to}`;
    if (statuses) url += `&statuses=${statuses}`;
    if (excludeStatuses) url += `&excludeStatuses=${excludeStatuses}`;
    return api.get(url);
  },
  searchBookings: (params) => api.get('/bookings/search', { params }),
  getBookingById: (id) => api.get(`/bookings/${id}`),
  getBookingsByTalentId: (talentId) => api.get(`/bookings/talent/${talentId}`),
  getBookingsByTalentIdAndStatus: (talentId, status) => api.get(`/bookings/talent/${talentId}/status/${status}`),
  getBookingsByTalentFromDate: (talentId, fromDate) => api.get(`/bookings/talent/${talentId}/from-date/${fromDate}`),
  createBooking: (bookingData) => {
    // Ensure the data structure matches the API requirements
    const formattedData = {
      // Required fields
      title: bookingData.title || "New Booking",
      talent_id: bookingData.talent_id || "pending",
      start_date: bookingData.start_date,
      status: bookingData.status || "new order",
      
      // Optional fields
      end_date: bookingData.end_date || bookingData.start_date,
      start_time: bookingData.start_time || "",
      end_time: bookingData.end_time || "",
      kpi: bookingData.kpi || 0,
      set: bookingData.set || "",
      set_desc: bookingData.set_desc || "",
      
      // JSON fields - ensure these are objects
      position: typeof bookingData.position === 'object' ? bookingData.position : {},
      client: typeof bookingData.client === 'object' ? bookingData.client : {},
      cost_center1: typeof bookingData.cost_center1 === 'object' ? bookingData.cost_center1 : {},
      cost_center2: typeof bookingData.cost_center2 === 'object' ? bookingData.cost_center2 : {},
      
      // Additional fields
      option: bookingData.option || "",
      location: bookingData.location || "",
      studio: bookingData.studio || "",
      sonstige_info: bookingData.sonstige_info || "",
      kind_info: bookingData.kind_info ?? bookingData.kindInfo ?? null,
      created_by: bookingData.created_by || null,
      order_id: bookingData.order_id || "",
      proposed_by: bookingData.proposed_by || "",
      group: bookingData.group || "",
      range: bookingData.range || ""
    };
    
    // Detailed logging of the request data
    console.log('📬 BOOKING REQUEST - Original Data:', bookingData);
    console.log('📬 BOOKING REQUEST - Formatted Data:', formattedData);
    console.log('📬 BOOKING REQUEST - JSON:', JSON.stringify(formattedData, null, 2));
    
    // Log any missing required fields
    const requiredFields = ['title', 'talent_id', 'start_date', 'status'];
    const missingFields = requiredFields.filter(field => !formattedData[field]);
    if (missingFields.length > 0) {
      console.error('❌ BOOKING REQUEST - Missing required fields:', missingFields);
    }
    
    // Log any potentially problematic fields
    if (typeof formattedData.start_date !== 'string') {
      console.warn('⚠️ BOOKING REQUEST - start_date is not a string:', formattedData.start_date);
    }
    
    if (typeof formattedData.end_date !== 'string') {
      console.warn('⚠️ BOOKING REQUEST - end_date is not a string:', formattedData.end_date);
    }
    
    return api.post('/bookings', formattedData);
  },
  updateBooking: (id, bookingData) => api.put(`/bookings/${id}`, bookingData),
  updateBookingStatus: (id, status) => api.patch(`/bookings/${id}/status`, { status }),
  deleteBooking: (id) => api.delete(`/bookings/${id}`),
  getBookingCategories: () => api.get('/bookings/categories'),
  getLocations: () => api.get('/locations'),
  sendEmail: (emailData) => api.post('/bookings/notifications/email', emailData),
  
  // New group-related methods
  addToGroup: (groupData) => api.post('/bookings/group', groupData),
  removeFromGroup: (id) => api.delete(`/bookings/${id}/group`),
  
  // Booking processing status endpoints
  startProcessing: (id) => api.post(`/bookings/${id}/processing/start`, {
    user_id: localStorage.getItem("userEmail"),
    token: localStorage.getItem("token")
  }),
  stopProcessing: (id) => api.post(`/bookings/${id}/processing/stop`, {
    user_id: localStorage.getItem("userEmail"),
    token: localStorage.getItem("token")
  }),
  getProcessingStatus: (id) => api.get(`/bookings/${id}/processing`),
  
  createLanoTask: (taskData, token) => api.post('/bookings/lano/tasks', taskData, {
    headers: { 'X-Lano-Auth': token }
  }),
  sendNotification: (bookingId, notificationData) => api.post(`/bookings/${bookingId}/notifications`, notificationData),
};

// Company services
export const companyService = {
  getAllCompanies: (page = 1, limit = 20) => api.get(`/companies?page=${page}&limit=${limit}`),
  getCompanyById: (id) => api.get(`/companies/${id}`),
  createCompany: (companyData) => api.post('/companies', companyData),
  updateCompany: (id, companyData) => api.put(`/companies/${id}`, companyData),
  deleteCompany: (id) => api.delete(`/companies/${id}`),
};

// Company Group services
export const companyGroupService = {
  getAllGroups: (page = 1, limit = 100) => api.get(`/company-groups?page=${page}&limit=${limit}`),
  getGroupById: (id) => api.get(`/company-groups/${id}`),
  createGroup: (groupData) => api.post('/company-groups', groupData),
  updateGroup: (id, groupData) => api.put(`/company-groups/${id}`, groupData),
  deleteGroup: (id) => api.delete(`/company-groups/${id}`),
  searchGroups: (searchTerm, page = 1, limit = 100) => api.get(`/company-groups/search?q=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`),
};

// Talent Group services
export const talentGroupService = {
  getAllGroups: (page = 1, limit = 100) => api.get(`/talent-groups?page=${page}&limit=${limit}`),
  getGroupById: (id) => api.get(`/talent-groups/${id}`),
  createGroup: (groupData) => api.post('/talent-groups', groupData),
  updateGroup: (id, groupData) => api.put(`/talent-groups/${id}`, groupData),
  deleteGroup: (id) => api.delete(`/talent-groups/${id}`),
  searchGroups: (searchTerm, page = 1, limit = 100) => api.get(`/talent-groups/search?q=${encodeURIComponent(searchTerm)}&page=${page}&limit=${limit}`),
};

// Location services
export const locationService = {
  getAllLocations: (page = 1, limit = 20) => api.get(`/locations?page=${page}&limit=${limit}`),
  getLocationById: (id) => api.get(`/locations/${id}`),
  createLocation: (locationData) => api.post('/locations', locationData),
  updateLocation: (id, locationData) => api.put(`/locations/${id}`, locationData),
  deleteLocation: (id) => api.delete(`/locations/${id}`),
};

// File upload services
export const uploadService = {
  uploadExcel: (fileData, description = '') => {
    
    const formData = new FormData();
    formData.append('file', fileData);
    formData.append('description', description);

    return api.post('/uploads/excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  },
  getExcelFiles: (page = 1, limit = 10, status = 'active', uploaderId = null) => {
    let url = `/uploads/excel?page=${page}&limit=${limit}&status=${status}`;
    if (uploaderId) url += `&uploaderId=${uploaderId}`;
    return api.get(url);
  },
  previewExcelFile: (id) => api.get(`/uploads/excel/preview/${id}`),
  downloadExcelFile: (id) => api.get(`/uploads/excel/download/${id}`, {
    responseType: 'blob', // Important for file downloads
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }
  }),
  deleteExcelFile: (id) => api.delete(`/uploads/excel/${id}`),
};

// Note services
export const noteService = {
  // Get notes for a talent on a specific date
  getTalentNotesByDate: (talentId, date) => {
    if (!talentId || !date) {
      return Promise.reject(new Error('Talent ID and date are required'));
    }
    
    return api.get(`/notes/talent/${talentId}/date/${date}`);
  },
  
  // Get notes for a talent across a date range (e.g., a week)
  getTalentNotesByDateRange: (talentId, startDate, endDate, page = 1, limit = 50) => {
    if (!talentId || !startDate || !endDate) {
      return Promise.reject(new Error('Talent ID, start date, and end date are required'));
    }
    
    return api.get(`/notes/talent/${talentId}/range/${startDate}/${endDate}`, {
      params: { page, limit }
    });
  },
  
  // Create a new note for a talent
  createNote: (noteData) => {
    if (!noteData.talent_id || !noteData.date || !noteData.content) {
      return Promise.reject(new Error('Talent ID, date, and content are required'));
    }
    
    return api.post('/notes', noteData);
  },
  
  // Update an existing note
  updateNote: (noteId, noteData) => {
    if (!noteId) {
      return Promise.reject(new Error('Note ID is required'));
    }
    
    return api.put(`/notes/${noteId}`, noteData);
  },
  
  // Delete a note
  deleteNote: (noteId) => {
    if (!noteId) {
      return Promise.reject(new Error('Note ID is required'));
    }
    
    return api.delete(`/notes/${noteId}`);
  }
};

// Utility function to check auth state
export const checkAuthState = () => {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  
  console.log('=== Auth State Check ===');
  console.log('Token exists:', !!token);
  console.log('User role:', userRole);
  console.log('Is authenticated:', isAuthenticated);
  console.log('=======================');
  
  return {
    isAuthenticated: !!token && isAuthenticated === 'true',
    userRole,
    hasToken: !!token
  };
};

// Talent Schedule services
export const talentScheduleService = {
  // Availability endpoints
  addAvailability: (availabilityData) => {
    if (!availabilityData.talent_id || !availabilityData.start_date || !availabilityData.end_date) {
      console.error('Missing required fields for addAvailability:', availabilityData);
      return Promise.reject(new Error('Talent ID, start and end dates are required'));
    }
    console.log('Adding availability with data:', availabilityData);
    return api.post('/talent-schedule/availability', availabilityData);
  },
  
  updateAvailability: (talentId, availabilityData) => {
    // Ensure we have all required fields
    if (!availabilityData.start_date || !availabilityData.end_date) {
      console.error('Missing required fields for updateAvailability:', availabilityData);
      return Promise.reject(new Error('Start and end dates are required'));
    }

    // Make sure the talent_id is included in the data
    const data = {
      ...availabilityData,
      talent_id: talentId
    };

    // Log formatted data for debugging
    console.log('Updating availability with data:', data);
    
    return api.put(`/talent-schedule/availability/${talentId}`, data);
  },
  
  deleteAvailability: (id) => api.delete(`/talent-schedule/availability/${id}`),

  // Vacation endpoints
  addVacation: (vacationData) => {
    if (!vacationData.talent_id || !vacationData.start_date || !vacationData.end_date) {
      console.error('Missing required fields for addVacation:', vacationData);
      return Promise.reject(new Error('Talent ID, start and end dates are required'));
    }
    console.log('Adding vacation with data:', vacationData);
    return api.post('/talent-schedule/vacation', vacationData);
  },
  
  updateVacation: (id, vacationData) => api.put(`/talent-schedule/vacation/${id}`, vacationData),
  
  deleteVacation: (id) => api.delete(`/talent-schedule/vacation/${id}`),

  // Awaiting endpoints
  addAwaiting: (awaitingData) => {
    if (!awaitingData.talent_id || !awaitingData.start_date || !awaitingData.end_date) {
      console.error('Missing required fields for addAwaiting:', awaitingData);
      return Promise.reject(new Error('Talent ID, start and end dates are required'));
    }
    console.log('Adding awaiting with data:', awaitingData);
    return api.post('/talent-schedule/awaiting', awaitingData);
  },
  
  updateAwaiting: (id, awaitingData) => api.put(`/talent-schedule/awaiting/${id}`, awaitingData),
  
  deleteAwaiting: (id) => api.delete(`/talent-schedule/awaiting/${id}`),

  // Secondoption endpoints
  addSecondoption: (secondoptionData) => {
    if (!secondoptionData.talent_id || !secondoptionData.start_date || !secondoptionData.end_date) {
      console.error('Missing required fields for addSecondoption:', secondoptionData);
      return Promise.reject(new Error('Talent ID, start and end dates are required'));
    }
    console.log('Adding secondoption with data:', secondoptionData);
    return api.post('/talent-schedule/secondoption', secondoptionData);
  },
  
  updateSecondoption: (id, secondoptionData) => api.put(`/talent-schedule/secondoption/${id}`, secondoptionData),
  
  deleteSecondoption: (id) => api.delete(`/talent-schedule/secondoption/${id}`),

  // Get schedule for a specific date
  getTalentScheduleForDate: (talentId, date) => {
    if (!talentId || !date) {
      console.error('Missing required parameters for getTalentScheduleForDate', { talentId, date });
      return Promise.reject(new Error('Talent ID and date are required'));
    }
    
    console.log(`Fetching schedule for talent ${talentId} on date ${date}`);
    
    return api.get(`/talent-schedule/date`, {
      params: {
        talent_id: talentId,
        date: date
      }
    }).catch(error => {
      console.warn(`Error fetching schedule for date ${date}:`, error);
      return { data: { schedule: { availabilities: [], vacations: [], awaitings: [] } } };
    });
  },

  // Get schedule for multiple dates
  getTalentScheduleForDates: async (talentId, dates) => {
    const results = await Promise.all(
      dates.map(date => 
        api.get('/talent-schedule/date', {
          params: {
            talent_id: talentId,
            date: date
          }
        })
      )
    );
    return results.map(response => response.data);
  },

  // Get schedule for a date range
  getTalentScheduleByRange: (talentId, startDate, endDate) => {
    if (!talentId || !startDate || !endDate) {
      console.error('Missing required parameters for getTalentScheduleByRange', { talentId, startDate, endDate });
      return Promise.reject(new Error('Talent ID, start date, and end date are required'));
    }
    
    console.log(`Fetching schedule for talent ${talentId} from ${startDate} to ${endDate}`);
    
    return api.get(`/talent-schedule/range`, {
      params: {
        talent_id: talentId,
        start_date: startDate,
        end_date: endDate
      }
    }).catch(error => {
      console.warn(`Error fetching schedule for range ${startDate} to ${endDate}:`, error);
      return { 
        data: {
          success: true,
          talent_id: talentId,
          schedule: { 
            availabilities: [], 
            vacations: [], 
            awaitings: [],
            bookings: []
          }
        }
      };
    });
  },
};

// Email services
export const emailService = {
  sendEmail: (emailData) => {
    // Ensure required fields are present
    if (!emailData.to_email || !emailData.subject || !emailData.content) {
      return Promise.reject(new Error('Missing required email fields'));
    }

    // Set default from_email if not provided
    const data = {
      from_email: emailData.from_email || 'noreply@set-cast.com',
      to_email: emailData.to_email,
      subject: emailData.subject,
      content: emailData.content,
      // Optional fields
      cc: emailData.cc,
      bcc: emailData.bcc,
      attachments: emailData.attachments,
      template_id: emailData.template_id,
      template_data: emailData.template_data
    };

    return api.post('/emails', data);
  },

  // Get email status
  getEmailStatus: (emailId) => api.get(`/emails/${emailId}`),

  // Get all emails (with optional filters)
  getEmails: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.to_email) params.append('to_email', filters.to_email);
    
    return api.get('/emails', { params });
  },

  // Retry failed email
  retryEmail: (emailId) => api.post(`/emails/${emailId}/retry`),

  // Cancel pending email
  cancelEmail: (emailId) => api.post(`/emails/${emailId}/cancel`)
};

export default api; 